import { format, startOfDay, subDays } from 'date-fns';

// Convert JS getDay() (0=Sun..6=Sat) to app convention (0=Mon..6=Sun)
export const toAppDayIndex = (date: Date) => {
  const d = date.getDay();
  return d === 0 ? 6 : d - 1;
};

export interface StreakInput {
  data: string | Date;
}

export interface StreakResult {
  current: number;
  longest: number;
  studiedToday: boolean;
}

/**
 * Calculates current and longest study streaks honoring the user's rest day.
 * Rest days are "free passes": they neither break the streak nor count as studied.
 *
 * - `current`: walks backwards from today. Today without a session does NOT
 *   break the streak (we just continue with yesterday). Other non-rest days
 *   without a session end the streak.
 * - `longest`: walks studied dates ascending; gaps composed only of rest
 *   days are considered continuous.
 */
export function calcStreak(
  sessoes: StreakInput[],
  diaDescanso: number,
): StreakResult {
  const isRestDay = (date: Date) => toAppDayIndex(date) === diaDescanso;

  const studiedDates = new Set(
    sessoes.map((s) => format(new Date(s.data), 'yyyy-MM-dd')),
  );
  const today = startOfDay(new Date());
  const todayKey = format(today, 'yyyy-MM-dd');
  const studiedToday = studiedDates.has(todayKey);

  // Current streak
  let current = 0;
  let checkDate = today;
  const MAX_DAYS = 800;
  for (let i = 0; i < MAX_DAYS; i++) {
    const key = format(checkDate, 'yyyy-MM-dd');
    const rest = isRestDay(checkDate);
    const studied = studiedDates.has(key);
    if (studied) {
      current++;
    } else if (rest) {
      // skip rest day
    } else {
      if (i === 0) {
        // Today not studied yet — keep streak from yesterday onward
        checkDate = startOfDay(subDays(checkDate, 1));
        continue;
      }
      break;
    }
    checkDate = startOfDay(subDays(checkDate, 1));
  }

  // Longest streak
  const sortedAsc = [...studiedDates].sort();
  let longest = 0;
  let temp = 0;
  for (let i = 0; i < sortedAsc.length; i++) {
    if (i === 0) {
      temp = 1;
    } else {
      const prev = startOfDay(new Date(sortedAsc[i - 1]));
      const curr = startOfDay(new Date(sortedAsc[i]));
      const diffDays = Math.round(
        (curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24),
      );
      if (diffDays === 1) {
        temp += 1;
      } else if (diffDays > 1) {
        let allRest = true;
        for (let k = 1; k < diffDays; k++) {
          const mid = startOfDay(subDays(curr, k));
          if (!isRestDay(mid)) {
            allRest = false;
            break;
          }
        }
        temp = allRest ? temp + 1 : 1;
      } else {
        temp = 1;
      }
    }
    longest = Math.max(longest, temp);
  }

  // Safety: the all-time record can never be smaller than the streak the
  // user is currently on. The ascending walk above iterates only over dates
  // present in `studiedDates`, so an ongoing streak that includes rest-day
  // passes at its boundaries can be undercounted. Clamping with `current`
  // fixes the "Recorde menor que sequência atual" bug on the dashboard.
  longest = Math.max(longest, current);

  return { current, longest, studiedToday };
}
